// Considered: StatusBadgeAtom — rejected. This section renders four distinct states
// (unresolved / degraded / private / listed-in-N with per-channel navigation links, plus an
// optional "List in channel" action), not a single label+tone pair; StatusBadgeAtom has no
// slot for a channel list or an action button. This is an EXTRACTION, not a new creation —
// lifted verbatim from `StudioTemplateDrawerOrganism`'s inline `renderDistribution` (the only
// other place this markup existed) so both call sites share one implementation. See spec
// Design System Reuse Audit (gap analysis item 13).
import React from 'react';
import { useTranslation } from 'localization';
import type { ChannelItemLookupEntry } from '../../../api/marketplaceApi';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { resolveLocalizedString } from '../../../utils/resolveLocalizedString';
import './DistributionStatusMolecule.scss';

function channelLabel(channel: ChannelItemLookupEntry, locale: string): string {
  return resolveLocalizedString(channel.channel_name, channel.channel_system_name, locale);
}

export interface DistributionStatusMoleculeProps {
  /** Batched/single distribution-lookup result. `null` = unresolved (still loading or the
   * lookup hasn't run) unless `degraded` is set, in which case it means "lookup failed." */
  channels: ChannelItemLookupEntry[] | null;
  /** True when the lookup call itself failed (never render "Private" in this case — fail
   * closed, per the anti-fabrication rule: an unknown listing state is not the same as
   * a known-private one). */
  degraded?: boolean;
  /** Navigate to a channel's detail page. Omit to render channel names as plain text. */
  onNavigateToChannel?: (channelId: string) => void;
  /** Opens the List-in-channel dialog. Omit to hide the action entirely (e.g. the read-only
   * browse drawer, which never offers this affordance). */
  onListInChannel?: () => void;
}

/**
 * Renders a template/context's channel distribution status: unresolved, degraded (lookup
 * failed), private (zero channels), or listed-in-N (with per-channel navigation). Shared by
 * `StudioTemplateDrawerOrganism` (read-only browse) and `StudioContextEditorPage`'s
 * Distribution tab (adds `onListInChannel`).
 */
const DistributionStatusMolecule: React.FC<DistributionStatusMoleculeProps> = ({
  channels,
  degraded = false,
  onNavigateToChannel,
  onListInChannel,
}) => {
  const { t, i18n } = useTranslation();

  const unresolved = !degraded && channels === null;

  return (
    <section className="distribution-status" data-testid="distribution-status">
      <h3 className="distribution-status__section-header">
        {t('studio.gallery.drawer.distribution.title')}
      </h3>

      {(degraded || unresolved) && (
        <output className="distribution-status__degraded">
          {t('studio.gallery.distributionDegraded')}
        </output>
      )}

      {!degraded && channels !== null && channels.length === 0 && (
        <p className="distribution-status__private">{t('studio.gallery.distribution.private')}</p>
      )}

      {!degraded && channels !== null && channels.length > 0 && (
        <ul className="distribution-status__channel-list">
          {channels.map((channel) => (
            <li key={channel.id}>
              {onNavigateToChannel ? (
                // The PWA mounts a HashRouter, so an `href` to an in-app path triggers a
                // full document load of a route the SPA never serves. Navigate in-router.
                <button
                  type="button"
                  className="distribution-status__channel-link"
                  onClick={() => onNavigateToChannel(channel.channel_id)}
                >
                  {channelLabel(channel, i18n.language)}
                </button>
              ) : (
                <span className="distribution-status__channel-name">
                  {channelLabel(channel, i18n.language)}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {onListInChannel && !degraded && (
        <ButtonAtom
          label={t('studio.gallery.drawer.distribution.listInChannel')}
          icon="pi pi-share-alt"
          variant="text"
          size="small"
          onClick={onListInChannel}
          className="distribution-status__list-action"
        />
      )}
    </section>
  );
};

DistributionStatusMolecule.displayName = 'DistributionStatusMolecule';

export default DistributionStatusMolecule;
